import React, {PropsWithChildren, useEffect, useMemo, useState} from "react";
import {HitTracker, WithLocationChange} from "../../../components/hit-tracker/HitTracker";
import {Pointer} from "../../../components/pointer/Pointer";
import {Vertex} from "../../../interfaces/Floor";
import {Line} from "../../../components/Line";
import * as THREE from "three";
import {Text} from "@react-three/drei";

export type WithVertices = {
    vertices: Vertex[];
};

type MutableState = {
    lineLength: number | null;
}

const referenceLocation = new THREE.Vector3();
const mutableState: MutableState = {
    lineLength: null,
};

export const ARFloorMarker = (props: PropsWithChildren<WithLocationChange & WithVertices>) => {

    const [currentCursorLocation, setCurrentCursorLocation] = useState<[number, number, number]>([0, 0, 0]);
    const [currentLineLength, setCurrentLineLength] = useState<number | null>(null);

    const firstVertex = useMemo(() => props.vertices[0], [props.vertices]);
    const lastVertex = useMemo(() => props.vertices[props.vertices.length - 1], [props.vertices]);

    const lines = useMemo(() => props.vertices.map(({x, y, z}, index) => {
        const nextVertex = props.vertices[index + 1];
        if (!nextVertex) {
            return null;
        }

        const {x: x1, y: y1, z: z1} = nextVertex;

        return (<Line key={`line-${index}`} startLine={[x, y, z]} endLine={[x1, y1, z1]} color={"orange"}/>);
    }), [props.vertices]);

    const onLocationChanged = (loc: THREE.Matrix4) => {
        if (lastVertex) {
            const {x, y, z} = lastVertex;
            const lastVertexVector = new THREE.Vector3(x, y, z);
            const distance = lastVertexVector.distanceTo(new THREE.Vector3().setFromMatrixPosition(loc));
            mutableState.lineLength = distance;
        }

        if (props.vertices.length < 2) {
            referenceLocation.setFromMatrixPosition(loc);
            props.onLocationChanged(loc);
            return;
        }

        const {x, y, z} = firstVertex;
        const lastVertexVector = new THREE.Vector3(x, y, z);
        const distance = lastVertexVector.distanceTo(new THREE.Vector3().setFromMatrixPosition(loc));

        if (distance > 0.1) {
            referenceLocation.setFromMatrixPosition(loc);
            props.onLocationChanged(loc);
            return;
        }

        const newLoc = loc.clone().setPosition(x, y, z);

        referenceLocation.setFromMatrixPosition(newLoc);
        props.onLocationChanged(newLoc);
    };

    useEffect(() => {

        const timer = setInterval(() => {
            if (lastVertex) {
                const {x, y, z} = referenceLocation;
                setCurrentCursorLocation([x, y, z]);
                setCurrentLineLength(mutableState.lineLength);
            }
        }, 750);

        return () => {
            clearInterval(timer);
        }
    });

    return (
        <>
            <HitTracker onLocationChanged={onLocationChanged}>
                <Pointer/>
                {currentLineLength && (
                    <Text color={"black"} anchorX={"center"} position={[0, 0.06, 0]} fontSize={0.1}>
                        {Math.ceil(currentLineLength * 100)} cm
                    </Text>
                )}
            </HitTracker>
            {lines}
            {lastVertex && (
                <Line key={"reference-line"} startLine={[lastVertex.x, lastVertex.y, lastVertex.z]} endLine={currentCursorLocation} color={"gray"} />
            )}
        </>
    );
};